import React from 'react';
import { Link } from 'react-router';
import EventIcon from '@mui/icons-material/Event';
import StorefrontIcon from '@mui/icons-material/Storefront';
import VideocamIcon from '@mui/icons-material/Videocam';
import StyleIcon from '@mui/icons-material/Style';
import GroupIcon from '@mui/icons-material/Group';
import { SEOHelmet } from 'components/SEO';
import { playPath } from 'utils/play/inviteUrl';
import type { PlayTournament } from 'types/interfaces/play';
import { STRUCTURE_OPTIONS } from 'constants/play';
import { PlayLayout } from '../../layout/PlayLayout';
import { PlayAuthCard } from '../../components/PlayAuthCard';
import { formatStartLine } from '../../playFormat';
import './InvitePage.css';

interface Props {
  code: string;
  tournament: PlayTournament;
  /** Registration is closed (status past the late-registration window or COMPLETE) */
  closed: boolean;
}

export const structureLine = (t: Pick<PlayTournament, 'structure' | 'swissRounds' | 'topCutSize'>): string => {
  const label = STRUCTURE_OPTIONS.find((s) => s.value === t.structure)?.label ?? t.structure;
  if (t.structure === 'SWISS') return `Swiss · ${t.swissRounds} rounds`;
  if (t.structure === 'SWISS_TOP_CUT') return `Swiss · ${t.swissRounds} rounds + Top ${t.topCutSize ?? 8}`;
  return label;
};

/** formatLabel is already resolved server-side (customFormatName when Other); the fallback covers an older payload. */
export const formatLabelOf = (t: Pick<PlayTournament, 'formatLabel' | 'customFormatName'>): string =>
  t.formatLabel === 'Other' && t.customFormatName ? t.customFormatName : t.formatLabel;

/** Facts card shared by the invite page, the lobby and the player home header. */
export const EventFacts: React.FC<{ tournament: PlayTournament; showCount?: boolean }> = ({ tournament, showCount = true }) => (
  <ul className="play-invite__facts">
    <li>
      <EventIcon />
      <span>{formatStartLine(tournament.startsAt, tournament.timezone)}</span>
    </li>
    <li>
      {tournament.venueType === 'ONLINE' ? <VideocamIcon /> : <StorefrontIcon />}
      <span>{tournament.venueText || (tournament.venueType === 'ONLINE' ? 'Online event' : 'Location TBD')}</span>
    </li>
    <li>
      <StyleIcon />
      <span>
        {formatLabelOf(tournament)} · {structureLine(tournament)}
      </span>
    </li>
    {showCount && (
      <li>
        <GroupIcon />
        <span>{tournament.registeredCount} registered</span>
      </li>
    )}
  </ul>
);

/**
 * The invite page for a visitor who is not signed in (Decisions 3, 4, 24).
 * The Play auth card (Continue with Discord, the existing TradeMagic login)
 * returns to this page after auth.
 */
const InvitePage: React.FC<Props> = ({ code, tournament, closed }) => {
  const here = playPath(code);

  return (
    <PlayLayout>
      <SEOHelmet title={`${tournament.name} - TradeMagic Play`} description={`Join ${tournament.name} on TradeMagic Play.`} noindex />
      <section className="play-card play-invite__event" data-testid="play-invite">
        <p className="play-eyebrow">You're invited</p>
        <h1 className="play-invite__name">{tournament.name}</h1>
        <EventFacts tournament={tournament} />
      </section>

      {closed ? (
        <section className="play-card play-invite__closed" data-testid="play-invite-closed">
          <h2 className="play-invite__join-title">{tournament.status === 'COMPLETE' ? 'This event has finished' : 'Registration has closed'}</h2>
          <p className="play-helper">
            {tournament.publicPage ? 'You can still follow pairings and standings live.' : 'Ask the organizer if you were expecting to play.'}
          </p>
          {tournament.publicPage && (
            <p style={{ marginTop: 12 }}>
              <Link to={playPath(code, 'live')} className="play-secondary-button">
                Follow live
              </Link>
            </p>
          )}
        </section>
      ) : (
        <PlayAuthCard returnTo={here} />
      )}
    </PlayLayout>
  );
};

export default InvitePage;
